<script setup>
import { ref, computed, onMounted, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useTasksStore } from 'dashboard/stores/tasks';
import { getTaskDueBucket } from 'dashboard/composables/useTaskDueDate';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TasksListLayout from 'dashboard/components-next/Tasks/TasksListLayout.vue';
import TaskTable from 'dashboard/components-next/Tasks/TasksTable/TaskTable.vue';
import TaskKanbanBoard from 'dashboard/components-next/Tasks/TasksKanban/TaskKanbanBoard.vue';
import TaskEditModal from 'dashboard/components-next/Tasks/TaskEditModal.vue';
import TaskCreateModal from 'dashboard/components-next/Tasks/TaskCreateModal.vue';

const DEFAULT_SORT_FIELD = 'due_at';
const KANBAN_PAGE_SIZE = 100;

const tasksStore = useTasksStore();
const { t } = useI18n();
const { updateUISettings, uiSettings } = useUISettings();

const tasks = computed(() => tasksStore.getTasksList);
const meta = computed(() => tasksStore.getMeta);
const uiFlags = computed(() => tasksStore.getUIFlags);
const isFetchingList = computed(() => uiFlags.value.fetchingList);

// Approximate, at-a-glance counts from whichever tasks are already loaded
// (a full page in list view, up to 100 in kanban view) — not an exact
// account-wide total, deliberately avoided a dedicated counts endpoint here.
const openCount = computed(
  () => tasks.value.filter(task => task.status !== 'done').length
);
const overdueCount = computed(
  () =>
    tasks.value.filter(task => getTaskDueBucket(task) === 'overdue').length
);

const { tasks_view_mode: savedViewMode = 'list' } = uiSettings.value ?? {};
const viewMode = ref(savedViewMode);

const pageNumber = ref(1);

const parseSortSettings = (sortString = '') => {
  const hasDescending = sortString.startsWith('-');
  const sortField = hasDescending ? sortString.slice(1) : sortString;
  return {
    sort: sortField || DEFAULT_SORT_FIELD,
    order: hasDescending ? '-' : '',
  };
};

const { tasks_sort_by: taskSortBy = DEFAULT_SORT_FIELD } =
  uiSettings.value ?? {};
const { sort: initialSort, order: initialOrder } =
  parseSortSettings(taskSortBy);

const sortState = reactive({
  activeSort: initialSort,
  activeOrdering: initialOrder,
});

const filters = reactive({
  status: '',
  companyId: '',
  contactId: '',
  contactName: '',
  dueFrom: '',
  dueTo: '',
  owner: 'all',
});

// The owner filter is a single control with three meanings: everything,
// only mine, or a specific agent — each maps to a different backend param.
const ownerParams = () => {
  if (filters.owner === 'mine') return { scope: 'mine' };
  if (filters.owner && filters.owner !== 'all') {
    return { assignee_id: filters.owner };
  }
  return {};
};

const buildQueryParams = () => ({
  page: viewMode.value === 'kanban' ? 1 : pageNumber.value,
  per_page: viewMode.value === 'kanban' ? KANBAN_PAGE_SIZE : undefined,
  sort: sortState.activeSort,
  direction: sortState.activeOrdering === '-' ? 'desc' : 'asc',
  status: filters.status || undefined,
  company_id: filters.companyId || undefined,
  contact_id: filters.contactId || undefined,
  due_after: filters.dueFrom || undefined,
  due_before: filters.dueTo || undefined,
  ...ownerParams(),
});

const fetchTasks = async () => {
  try {
    await tasksStore.get(buildQueryParams());
  } catch (error) {
    useAlert(t('TASKS_DASHBOARD.LOADING'));
  }
};

const onPageChange = page => {
  pageNumber.value = page;
  fetchTasks();
};

const handleSort = async ({ sort, order }) => {
  Object.assign(sortState, { activeSort: sort, activeOrdering: order });
  await updateUISettings({
    tasks_sort_by: `${order}${sort}`,
  });
  pageNumber.value = 1;
  fetchTasks();
};

const handleViewModeChange = async mode => {
  viewMode.value = mode;
  await updateUISettings({ tasks_view_mode: mode });
  pageNumber.value = 1;
  fetchTasks();
};

const applyFilters = newFilters => {
  Object.assign(filters, newFilters);
  pageNumber.value = 1;
  fetchTasks();
};

const clearFilters = () => {
  Object.assign(filters, {
    status: '',
    companyId: '',
    contactId: '',
    contactName: '',
    dueFrom: '',
    dueTo: '',
    owner: 'all',
  });
  pageNumber.value = 1;
  fetchTasks();
};

const onStatusChange = async (taskId, status) => {
  try {
    await tasksStore.update({ id: taskId, status });
  } catch (error) {
    useAlert(t('TASKS_DASHBOARD.EMPTY_STATE.TITLE'));
  }
};

const onKanbanStatusChange = ({ taskId, status }) => {
  onStatusChange(taskId, status);
};

const taskEditModalRef = ref(null);
const selectedTask = ref({});

const openEditModal = task => {
  selectedTask.value = task;
  taskEditModalRef.value?.open();
};

const onSaveTask = async payload => {
  try {
    await tasksStore.update(payload);
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.TASKS.API.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.TASKS.API.ERROR_MESSAGE'));
  }
};

const taskCreateModalRef = ref(null);
const openCreateModal = (status = null) =>
  taskCreateModalRef.value?.open(status ? { status } : {});

const onTaskCreated = () => {
  pageNumber.value = 1;
  fetchTasks();
};

const onDelete = async taskId => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('CONTACTS_LAYOUT.SIDEBAR.TASKS.DELETE_CONFIRM'))) {
    return;
  }
  try {
    await tasksStore.delete(taskId);
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.TASKS.API.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.TASKS.API.ERROR_MESSAGE'));
  }
};

onMounted(() => {
  fetchTasks();
});
</script>

<template>
  <TasksListLayout
    :header-title="t('TASKS_DASHBOARD.HEADER')"
    :open-count="openCount"
    :overdue-count="overdueCount"
    :current-page="pageNumber"
    :total-items="Number(meta.totalCount || 0)"
    :active-sort="sortState.activeSort"
    :active-ordering="sortState.activeOrdering"
    :view-mode="viewMode"
    :filters="filters"
    :show-pagination-footer="!!tasks.length"
    @update:current-page="onPageChange"
    @update:sort="handleSort"
    @update:view-mode="handleViewModeChange"
    @apply-filters="applyFilters"
    @clear-filters="clearFilters"
    @create-task="openCreateModal"
  >
    <div
      v-if="isFetchingList"
      class="flex items-center justify-center gap-3 p-16 text-n-slate-11"
    >
      <Spinner />
      <span class="text-sm">{{ t('TASKS_DASHBOARD.LOADING') }}</span>
    </div>
    <div
      v-else-if="tasks.length === 0"
      class="flex flex-col items-center justify-center gap-3 p-16 text-center"
    >
      <span
        class="size-12 rounded-2xl bg-n-alpha-2 flex items-center justify-center text-n-slate-10"
      >
        <Icon icon="i-lucide-list-checks" size="size-6" />
      </span>
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('TASKS_DASHBOARD.EMPTY_STATE.TITLE') }}
      </span>
      <span class="text-sm text-n-slate-11 max-w-sm">
        {{ t('TASKS_DASHBOARD.EMPTY_STATE.SUBTITLE') }}
      </span>
    </div>
    <TaskKanbanBoard
      v-else-if="viewMode === 'kanban'"
      :tasks="tasks"
      @status-change="onKanbanStatusChange"
      @edit-task="openEditModal"
      @add-task="openCreateModal"
    />
    <TaskTable
      v-else
      :tasks="tasks"
      @status-change="onStatusChange"
      @delete="onDelete"
      @edit="openEditModal"
    />
  </TasksListLayout>

  <TaskEditModal
    ref="taskEditModalRef"
    :task="selectedTask"
    @save="onSaveTask"
  />
  <TaskCreateModal ref="taskCreateModalRef" @created="onTaskCreated" />
</template>
